import { useRef, useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import toast from "../../utils/toast";
import { getChangePassword } from "../../services/tenant/tenantServices";

const navItems = [
  {
    section: "My Account",
    items: [
      {
        id: "tn-dashboard",
        icon: "▣",
        label: "Dashboard",
        path: "/tenant/dashboard",
      },
      {
        id: "tn-bills",
        icon: "◐",
        label: "My Bills",
        path: "/tenant/my-bills",
      },
      {
        id: "tn-payments",
        icon: "◑",
        label: "Pay Rent / Fund",
        path: "/tenant/pay-rent",
      },
      {
        id: "tn-pay-history",
        icon: "◑",
        label: "Payment History",
        path: "/tenant/payment-history",
      },
      {
        id: "tn-kyc",
        icon: "◈",
        label: "KYC Documents",
        path: "/tenant/kyc-documents",
      },
      {
        id: "tn-vacate-notice",
        icon: "🚪",
        label: "Vacate Notice",
        path: "/tenant/vacate-notice",
      },
      {
        id: "tn-complaints",
        icon: "◎",
        label: "Complaints",
        path: "/tenant/complaints",
      },
    ],
  },
  {
    section: "Support",
    items: [
      {
        id: "tn-support",
        icon: "🎫",
        label: "Support Tickets",
        path: "/tenant/support-tickets",
      },
    ],
  },
  {
    section: "Account",
    items: [
      {
        id: "tn-wallet",
        icon: "🪙",
        label: "My Wallet",
        path: "/tenant/my-wallet",
      },
    ],
  },
];

export default function TenantSidebar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [showLogoutMenu, setShowLogoutMenu] = useState(false);
  const [showAvatarMenu, setShowAvatarMenu] = useState(false);
  const [showChangePwd, setShowChangePwd] = useState(false);

  const [pwdForm, setPwdForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [pwdErrors, setPwdErrors] = useState({});
  const [pwdLoading, setPwdLoading] = useState(false);
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const logoutRef = useRef(null);
  const avatarRef = useRef(null);

  const closeSidebar = () => setMobileOpen(false);

  useEffect(() => {
    function handleClickOutside(e) {
      if (logoutRef.current && !logoutRef.current.contains(e.target)) {
        setShowLogoutMenu(false);
      }
      if (avatarRef.current && !avatarRef.current.contains(e.target)) {
        setShowAvatarMenu(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = () => {
    logout();
    setShowLogoutMenu(false);
    navigate("/");
  };

  const handleLogoutAll = () => {
    logout();
    setShowLogoutMenu(false);
    navigate("/");
  };

  const handleProfile = () => {
    setShowAvatarMenu(false);
    navigate("/tenant/tenant-profile");
  };

  const openChangePwd = () => {
    setShowAvatarMenu(false);
    setPwdForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
    setPwdErrors({});
    setShowChangePwd(true);
  };

  const closeChangePwd = () => {
    setShowChangePwd(false);
    setPwdForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
    setPwdErrors({});
  };

  const handlePwdChange = (e) => {
    setPwdForm({ ...pwdForm, [e.target.name]: e.target.value });
    setPwdErrors({ ...pwdErrors, [e.target.name]: "" });
  };

  const validatePwd = () => {
    const e = {};
    if (!pwdForm.currentPassword)
      e.currentPassword = "Current password is required.";
    if (!pwdForm.newPassword) e.newPassword = "New password is required.";
    else if (pwdForm.newPassword.length < 8)
      e.newPassword = "Minimum 8 characters required.";
    if (!pwdForm.confirmPassword)
      e.confirmPassword = "Please confirm your new password.";
    else if (pwdForm.newPassword !== pwdForm.confirmPassword)
      e.confirmPassword = "Passwords do not match.";
    setPwdErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleChangePwd = async () => {
    if (!validatePwd()) return;
    setPwdLoading(true);
    try {
      const res = await getChangePassword({
        currentPassword: pwdForm.currentPassword,
        newPassword: pwdForm.newPassword,
        confirmPassword: pwdForm.confirmPassword,
      });
      if (res.statusCode !== 1) {
        toast.error(res.message || "Failed to change password.");
        return;
      }
      toast.success(res.message);
      closeChangePwd();
      setTimeout(() => {
        logout();
        navigate("/login");
      }, 1500);
    } catch (err) {
      toast.error(err.response?.data?.message || "Something went wrong.");
    } finally {
      setPwdLoading(false);
    }
  };

  const initials = user?.fullName
    ?.split(" ")
    .map((w) => w[0])
    .join("")
    .toUpperCase();

  return (
    <>
      <style>{`
        @keyframes fadeSlideUp {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .logout-dropdown {
          position: absolute;
          bottom: calc(100% + 8px);
          right: 0;
          background: #fff;
          border: 1px solid #e5e7eb;
          border-radius: 10px;
          box-shadow: 0 8px 24px rgba(0,0,0,0.13);
          min-width: 185px;
          z-index: 1000;
          overflow: hidden;
          animation: fadeSlideUp 0.18s ease;
        }
        .logout-option {
          display: flex;
          align-items: center;
          gap: 9px;
          padding: 11px 15px;
          font-size: 13.5px;
          cursor: pointer;
          border: none;
          background: none;
          width: 100%;
          text-align: left;
          transition: background 0.15s;
          font-family: inherit;
        }
        .logout-option:hover { background: #f3f4f6; }
        .logout-option.danger { color: #dc2626; }
        .logout-option.danger:hover { background: #fef2f2; }
        .logout-divider { height: 1px; background: #e5e7eb; }
        .logout-chevron {
          display: inline-block;
          font-size: 10px;
          margin-left: 3px;
          transition: transform 0.2s;
        }
        .logout-chevron.open { transform: rotate(180deg); }
        .pwd-input-wrap { position: relative; }
        .pwd-input-wrap input { width: 100%; padding-right: 38px; }
        .pwd-eye-btn {
          position: absolute;
          right: 10px;
          top: 50%;
          transform: translateY(-50%);
          background: none;
          border: none;
          cursor: pointer;
          font-size: 15px;
          color: #888;
          padding: 0;
          line-height: 1;
        }
        .field-error {
          color: #dc2626;
          font-size: 11px;
          margin-top: 3px;
          display: block;
        }
      `}</style>

      <div className="hamburger" onClick={() => setMobileOpen(true)}>
        ☰
      </div>

      {mobileOpen && (
        <div
          onClick={closeSidebar}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.4)",
            zIndex: 998,
          }}
        />
      )}

      <nav className={`sidebar${mobileOpen ? " open" : ""}`} id="sidebar">
        <div className="sidebar-brand">
            <img src="/tenant/assets/image/logo.png" alt="Logo" height={30} />
        </div>

        <div
          className="role-indicator"
          style={{ background: "rgb(250, 238, 218)", color: "rgb(99, 56, 6)" }}
        >
          🏠 Tenant
        </div>

        <div id="sidebar-nav">
          {navItems.map((section) => (
            <div className="nav-section" key={section.section}>
              <div className="nav-label">{section.section}</div>
              {section.items.map((item) => (
                <NavLink
                  key={item.id}
                  to={item.path}
                  end
                  className={({ isActive }) =>
                    `nav-item${isActive ? " active" : ""}`
                  }
                  data-id={item.id}
                  onClick={closeSidebar}
                >
                  <span className="nav-icon">{item.icon}</span>
                  <span>{item.label}</span>
                </NavLink>
              ))}
            </div>
          ))}
        </div>

        <div className="sidebar-footer">
          <div className="admin-badge">
            {/* ── Avatar with dropdown ── */}
            <div ref={avatarRef} style={{ position: "relative" }}>
              <div
                className="s-avatar"
                style={{
                  background: "rgb(250, 199, 117)",
                  color: "rgb(99, 56, 6)",
                  cursor: "pointer",
                  overflow: "hidden",
                  padding: 0,
                }}
                onClick={() => setShowAvatarMenu((prev) => !prev)}
                title="Profile options"
              >
                {user?.profilePhoto ? (
                  <img
                    src={user.profilePhoto}
                    alt="avatar"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  initials || "T"
                )}
              </div>

              {showAvatarMenu && (
                <div
                  className="logout-dropdown"
                  style={{ bottom: "calc(100% + 8px)", left: 0, right: "auto" }}
                >
                  <button className="logout-option" onClick={handleProfile}>
                    <span>👤</span> My Profile
                  </button>
                  <div className="logout-divider" />
                  <button className="logout-option" onClick={openChangePwd}>
                    <span>🔑</span> Change Password
                  </button>
                </div>
              )}
            </div>

            <div>
              <div className="s-name">{user?.fullName}</div>
              <div className="s-role">{user?.role}</div>
            </div>

            {/* ── Logout with dropdown ── */}
            <div ref={logoutRef} style={{ position: "relative" }}>
              <button
                className="logout-btn"
                onClick={() => setShowLogoutMenu((prev) => !prev)}
              >
                Logout
                <span
                  className={`logout-chevron${showLogoutMenu ? " open" : ""}`}
                >
                  ▾
                </span>
              </button>

              {showLogoutMenu && (
                <div className="logout-dropdown">
                  <button
                    className="logout-option danger"
                    onClick={handleLogoutAll}
                  >
                    <span>🔴</span> Logout from All
                  </button>
                  <div className="logout-divider" />
                  <button className="logout-option" onClick={handleLogout}>
                    <span>🚪</span> Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="sidebar-close" onClick={closeSidebar}>
          ✖
        </div>
      </nav>

      {/* ── Change Password Modal ── */}
      {showChangePwd && (
        <div className="modal-overlay open">
          <div className="modal" style={{ width: 440 }}>
            <div className="modal-header">
              <div className="modal-title">Change Password</div>
              <button className="modal-close" onClick={closeChangePwd}>
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div
                className="info-box"
                style={{ display: "block", marginBottom: 16 }}
              >
                After changing your password you will be logged out and
                redirected to login.
              </div>
              <div className="form-grid">
                <div className="form-group full">
                  <label>Current Password</label>
                  <div className="pwd-input-wrap">
                    <input
                      type={showCurrent ? "text" : "password"}
                      name="currentPassword"
                      value={pwdForm.currentPassword}
                      onChange={handlePwdChange}
                      placeholder="Enter current password"
                      disabled={pwdLoading}
                      style={
                        pwdErrors.currentPassword
                          ? { borderColor: "#dc2626" }
                          : {}
                      }
                    />
                    <button
                      type="button"
                      className="pwd-eye-btn"
                      onClick={() => setShowCurrent((p) => !p)}
                    >
                      {showCurrent ? "🙈" : "👁"}
                    </button>
                  </div>
                  {pwdErrors.currentPassword && (
                    <span className="field-error">
                      {pwdErrors.currentPassword}
                    </span>
                  )}
                </div>

                <div className="form-group full">
                  <label>New Password</label>
                  <div className="pwd-input-wrap">
                    <input
                      type={showNew ? "text" : "password"}
                      name="newPassword"
                      value={pwdForm.newPassword}
                      onChange={handlePwdChange}
                      placeholder="Enter new password (min 8 characters)"
                      disabled={pwdLoading}
                      style={
                        pwdErrors.newPassword ? { borderColor: "#dc2626" } : {}
                      }
                    />
                    <button
                      type="button"
                      className="pwd-eye-btn"
                      onClick={() => setShowNew((p) => !p)}
                    >
                      {showNew ? "🙈" : "👁"}
                    </button>
                  </div>
                  {pwdErrors.newPassword && (
                    <span className="field-error">{pwdErrors.newPassword}</span>
                  )}
                </div>

                <div className="form-group full">
                  <label>Confirm New Password</label>
                  <div className="pwd-input-wrap">
                    <input
                      type={showConfirm ? "text" : "password"}
                      name="confirmPassword"
                      value={pwdForm.confirmPassword}
                      onChange={handlePwdChange}
                      placeholder="Re-enter new password"
                      disabled={pwdLoading}
                      style={
                        pwdErrors.confirmPassword
                          ? { borderColor: "#dc2626" }
                          : {}
                      }
                    />
                    <button
                      type="button"
                      className="pwd-eye-btn"
                      onClick={() => setShowConfirm((p) => !p)}
                    >
                      {showConfirm ? "🙈" : "👁"}
                    </button>
                  </div>
                  {pwdErrors.confirmPassword && (
                    <span className="field-error">
                      {pwdErrors.confirmPassword}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn"
                onClick={closeChangePwd}
                disabled={pwdLoading}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleChangePwd}
                disabled={pwdLoading}
              >
                {pwdLoading ? "Changing..." : "Change Password"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
